import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { PortfolioTab } from '@/lib/portfolio'

/**
 * Tabbed asset folder (Website / Print / Ads / Logo) — matches the live
 * site's `.pf-folder-tab-row` + `.pf-folder-panel` behavior.
 */
export function MediaTabs({
  tabs,
  onAssetClick,
}: {
  tabs: PortfolioTab[]
  onAssetClick: (image: string, alt: string) => void
}) {
  const { lang } = useLanguage()
  // Closed by default — nothing renders until a tab is actually clicked, so
  // this doesn't just repeat the hero image/before-after slider shown right
  // above it in ProjectFolder for every project on page load.
  const [active, setActive] = useState<string | null>(null)
  const activeTab = tabs.find((tab) => tab.id === active)

  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === active}
            onClick={() => setActive((v) => (v === tab.id ? null : tab.id))}
            className={`rounded-pill px-4 py-1.5 text-sm font-semibold transition-all duration-normal ${
              tab.id === active
                ? 'bg-accent-fire/15 text-accent-fire'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            {lang === 'en' ? tab.labelEn : tab.label}
          </button>
        ))}
      </div>

      {activeTab && (
        <div role="tabpanel" className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-5">
          {activeTab.assets.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => onAssetClick(asset.image, lang === 'en' ? asset.altEn : asset.alt)}
              className="group relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.02] text-left transition-all duration-normal hover:border-accent-fire/40"
            >
              <div className="flex aspect-square w-full items-center justify-center bg-black/20 p-2">
                <img
                  src={asset.image}
                  alt={lang === 'en' ? asset.altEn : asset.alt}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain transition-transform duration-normal group-hover:scale-105"
                />
              </div>
              <span className="block truncate px-2 py-1.5 text-[11px] font-medium text-foreground-muted">
                {lang === 'en' ? asset.labelEn : asset.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
